var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import DraggableToken from './DraggableToken';
import '../styles/draggableTokenList.scss';
// Map ColorEnum keys to asset folder names
var COLOR_ASSET_MAP = {
    RED: 'red',
    YELLOW: 'yellow',
    BLUE: 'blue',
    BROWN: 'brown',
    ORANGE: 'orange',
    GREEN: 'green',
    PURPLE: 'purple',
    BLACK: 'black',
    WHITE: 'white',
    GRAY: 'gray',
};
var TokenGroup = function (_a) {
    var items = _a.items, groupLabel = _a.groupLabel, dataGroup = _a.dataGroup, ariaLabel = _a.ariaLabel, keyboardInactive = _a.keyboardInactive;
    var _b = useState(0), focusedIdx = _b[0], setFocusedIdx = _b[1];
    var tokenRefs = useRef([]);
    // Clamp focusedIdx when items length shrinks (safety only — currently always 3).
    var safeFocusedIdx = Math.min(focusedIdx, items.length - 1);
    var moveFocus = function (next) {
        var n = items.length;
        var clamped = ((next % n) + n) % n; // wrap both ways
        setFocusedIdx(clamped);
        requestAnimationFrame(function () {
            var _a;
            (_a = tokenRefs.current[clamped]) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    var handleKeyDown = function (e) {
        var _a;
        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                moveFocus(safeFocusedIdx + 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                moveFocus(safeFocusedIdx - 1);
                break;
            case 'Home':
                e.preventDefault();
                e.stopPropagation();
                moveFocus(0);
                break;
            case 'End':
                e.preventDefault();
                e.stopPropagation();
                moveFocus(items.length - 1);
                break;
            case 'Escape': {
                // Close the matching token-setting-btn and return focus to it.
                // Button is outside the panel's `inert` region so focus survives the
                // state flip that re-renders the panel as inactive.
                e.preventDefault();
                e.stopPropagation();
                var btn = document.querySelector(".tokenSettingBtnWrapper.token-setting-".concat(dataGroup, " .token-setting-btn"));
                if (btn) {
                    btn.click();
                    btn.focus();
                }
                return;
            }
            case 'Tab':
                // Shift+Tab from a token goes *out* of the drawer-content section
                // naturally (to editNumberFrame, then another group, etc.). Redirect
                // it to the matching TokenSettingPanel's selected shape radio so the
                // reverse tab flow reads: token-group → shapes → colors → token-btn.
                // Forward Tab (no shift) falls through to native behavior.
                if (e.shiftKey) {
                    var shapeArea = document.querySelector(".tokenSettingBtnWrapper.token-setting-".concat(dataGroup, " .tokenShapeArea"));
                    if (shapeArea && !shapeArea.closest('[inert]')) {
                        var target = (_a = shapeArea.querySelector('button[role="radio"][tabindex="0"]')) !== null && _a !== void 0 ? _a : shapeArea.querySelector('button[role="radio"]');
                        if (target) {
                            e.preventDefault();
                            e.stopPropagation();
                            target.focus();
                            return;
                        }
                    }
                }
                return;
            default:
                // Enter / Space bubbles down to the focused DraggableToken, which
                // already maps them to placeOnFirstEmptyCell().
                return;
        }
    };
    return (_jsx("div", { className: "token-group", role: "group", "data-group": dataGroup, "aria-label": ariaLabel, "aria-hidden": keyboardInactive ? true : undefined, onKeyDown: handleKeyDown, children: items.map(function (item, idx) { return (_jsx(DraggableToken, __assign({}, item, { groupLabel: groupLabel, 
            // Only the focused token is a Tab stop, and only when the group is
            // keyboard-active. Mouse drag continues to work regardless because
            // we're not applying `inert`.
            tabbable: !keyboardInactive && idx === safeFocusedIdx, rootRef: function (el) {
                tokenRefs.current[idx] = el;
            }, onActivateFocus: function () { return setFocusedIdx(idx); } }), "".concat(dataGroup, "-").concat(item.variant, "-").concat(idx))); }) }));
};
var DraggableTokenList = function (_a) {
    var tokenSelections = _a.tokenSelections, _b = _a.tokenPanelOpen, tokenPanelOpen = _b === void 0 ? { top: false, bottom: false } : _b, _c = _a.hasRealNumberFrame, hasRealNumberFrame = _c === void 0 ? false : _c;
    var _d = useMemo(function () {
        var topItems = [];
        var bottomItems = [];
        if (tokenSelections.top.color && tokenSelections.top.shape) {
            var colorAsset = COLOR_ASSET_MAP[tokenSelections.top.color];
            topItems.push({ panel: 'top', variant: 1, color: colorAsset, shape: tokenSelections.top.shape }, { panel: 'top', variant: 5, color: colorAsset, shape: tokenSelections.top.shape }, { panel: 'top', variant: 10, color: colorAsset, shape: tokenSelections.top.shape });
        }
        if (tokenSelections.bottom.color && tokenSelections.bottom.shape) {
            var colorAsset = COLOR_ASSET_MAP[tokenSelections.bottom.color];
            bottomItems.push({ panel: 'bottom', variant: 1, color: colorAsset, shape: tokenSelections.bottom.shape }, { panel: 'bottom', variant: 5, color: colorAsset, shape: tokenSelections.bottom.shape }, { panel: 'bottom', variant: 10, color: colorAsset, shape: tokenSelections.bottom.shape });
        }
        return { topItems: topItems, bottomItems: bottomItems };
    }, [tokenSelections]), topItems = _d.topItems, bottomItems = _d.bottomItems;
    if (topItems.length === 0 && bottomItems.length === 0) {
        return null;
    }
    // Keyboard focus for a token-group is gated on the *corresponding*
    // token-setting-btn being active AND a real frame existing on the canvas.
    // Mouse drag ignores this — DraggableTokens stay draggable either way so
    // the existing "drag a token onto the canvas / frame" flow keeps working.
    var topKeyboardInactive = !hasRealNumberFrame || !tokenPanelOpen.top;
    var bottomKeyboardInactive = !hasRealNumberFrame || !tokenPanelOpen.bottom;
    var groupALabel = t('text.groupA');
    var groupBLabel = t('text.groupB');
    return (_jsxs(_Fragment, { children: [topItems.length > 0 && (_jsx(TokenGroup, { items: topItems, groupLabel: groupALabel, dataGroup: "top", ariaLabel: t('text.tokensForGroup', { group: groupALabel }), keyboardInactive: topKeyboardInactive })), bottomItems.length > 0 && (_jsx(TokenGroup, { items: bottomItems, groupLabel: groupBLabel, dataGroup: "bottom", ariaLabel: t('text.tokensForGroup', { group: groupBLabel }), keyboardInactive: bottomKeyboardInactive }))] }));
};
export default DraggableTokenList;
